'use client'

import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { Checkbox } from '@b-stock/fe-core/bstock-react'
import { Colors, Shadows } from '@b-stock/fe-core/design-system'

/**
 * White surface so the outline checkbox stays legible on light photos —
 * mirrors the treatment WatchlistCTA uses for the heart over these images.
 */
const OverlayContainer = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  /* Sit above the (pushed-down) BidStatusTag and card image. */
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.25rem;
  background-color: ${Colors.Semantic.Neutral.white};
  ${Shadows.MultipleCards}
  outline: 0.0625rem solid rgba(20, 26, 30, 0.05);
  outline-offset: -0.0625rem;

  /* No label text here, so drop the icon's trailing gap that would otherwise
     push the box off-centre. */
  svg {
    margin-right: 0;
  }

  /* The checkbox keeps focus after a click, and fe-core tints a focused glyph
     blue — so a just-deselected box stayed blue until something else took
     focus. Restore the resting colour for pointer focus only; keyboard focus
     still shows the ring. */
  input:not(:checked):focus:not(:focus-visible) + svg {
    color: ${Colors.Semantic.Neutral.dark_grey};
  }
`

type CompareCardOverlayProps = {
  isSelected: boolean
  isAtLimit: boolean
  onToggle: () => void
  listingId: string
  listingTitle: string
}

/**
 * Presentational compare checkbox rendered in the top-left of a listing card
 * while compare mode is active. Pure props in → three visual states out
 * (selected / available / disabled), delegating all rendering to the shared
 * fe-core `Checkbox` atom — the same one the table's per-row select uses.
 */
const CompareCardOverlay = ({
  isSelected,
  isAtLimit,
  onToggle,
  listingId,
  listingTitle,
}: CompareCardOverlayProps) => {
  const { formatMessage } = useIntl()

  const isDisabled = isAtLimit && !isSelected

  const ariaLabel = formatMessage(
    {
      id: isSelected
        ? 'Listings.ListView.compareMode.deselectCard'
        : 'Listings.ListView.compareMode.selectCard',
    },
    { title: listingTitle }
  )

  // A disabled <input> emits no onChange, but at the limit a click on the
  // (grayed) checkbox should still route through toggleListing so the limit
  // toast fires — matching a click on the card body. Only act when disabled;
  // otherwise the checkbox's own onChange drives the toggle.
  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDisabled) {
      e.preventDefault()
      e.stopPropagation()
      onToggle()
    }
  }

  return (
    <OverlayContainer
      data-testid="compare-card-overlay"
      data-listing-id={listingId}
      onClick={handleContainerClick}
    >
      <Checkbox
        checked={isSelected}
        disabled={isDisabled}
        onChange={onToggle}
        aria-label={ariaLabel}
        size={24}
      />
    </OverlayContainer>
  )
}

export default CompareCardOverlay
